import React, {useCallback, useEffect} from "react";
import {Outlet} from "react-router-dom";
import {
  Flex,
  Box,
} from "@chakra-ui/react";
import GlobalToaster from "../GlobalToaster/GlobalToaster.tsx";
import GlobalModal from "../GlobalModal/GlobalModal.tsx";
import GlobalDrawer from "../GlobalDrawer/GlobalDrawer.tsx";
import LeftSidebarMenu from "../LeftSidebarMenu/LeftSidebarMenu.tsx";
import GlobalHeader from "../GlobalHeader/GlobalHeader";
import useSystemStore from "../../store/useSystemStore.ts";
import {useSeriesStore} from "../../store/useSeriesStore.ts";
import useUiStore from "../../store/useUiStore.ts";
import InactivityModal from "../InactivityModal.tsx";

const AuthenticatedLayout: React.FC = () => {
  const {supportedCodeLanguages, getSupportedLanguages} = useSystemStore()
  const {modal, openModal} = useUiStore()
  const {seriesNameIdMapping, getSeriesNameIdMapping} = useSeriesStore()

  const presentInactivityModal = useCallback(() => {
    if (modal.isVisible) return; // don't stack / re-open while visible
    openModal(
      <InactivityModal/>,
      {
        classWrap: " min-h-20 p-6",
        classButtonClose: "hidden",
      }
    );
  }, [modal.isVisible, openModal]);

  useEffect(() => {
    if (supportedCodeLanguages.length === 0) {
      getSupportedLanguages().catch(console.error);
    }
  }, [supportedCodeLanguages.length, getSupportedLanguages]);

  useEffect(() => {
    if (Object.keys(seriesNameIdMapping).length === 0) {
      getSeriesNameIdMapping().catch(console.error);
    }
  }, [seriesNameIdMapping, getSeriesNameIdMapping]);

  // ---- Inactivity timer (paused when modal is open) ----
  useEffect(() => {
    // pause tracking while modal is open
    if (modal.isVisible) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const resetTimeout: () => void = (): void => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        presentInactivityModal();
      }, 10 * 60 * 1000); // 10 minutes
    };

    const events = ["click", "mousemove", "keydown", "scroll", "touchstart"] as const;
    events.forEach((ev) => window.addEventListener(ev, resetTimeout, {passive: true}));
    resetTimeout(); // start when user lands

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      events.forEach((ev) => window.removeEventListener(ev, resetTimeout));
    };
  }, [modal.isVisible, presentInactivityModal]);


  useEffect(() => {
    if (!modal.isVisible) {
      // dispatch a synthetic event so the effect above resets immediately
      window.dispatchEvent(new Event("mousemove"));
    }
  }, [modal.isVisible]);


  return (
    <div className="authenticated-layout">
      <Flex h="100%" w="100%" overflow="hidden">
        <Box as="aside" h="100vh" bg="n.700">
          <LeftSidebarMenu/>
        </Box>
        <Box as="main" flex="1" h="100vh" py={6} pr={6} minH={0}>
          <Box className="bg-n-100 dark:bg-n-600 h-full rounded-2xl px-9 flex flex-col overflow-hidden"
               style={{minHeight: 0}}>
            <GlobalHeader/>
            <Box className="flex-1 min-h-0 overflow-y-auto pb-2">
              <Outlet/>
            </Box>
          </Box>
        </Box>
        <GlobalToaster/>
        <GlobalModal/>
        <GlobalDrawer/>
      </Flex>
    </div>
  );
};

export default AuthenticatedLayout;